import { CompassOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useDebouncedCallback, useThrottledCallback } from 'use-debounce';

import { Divider, Flex, VFlex } from '@/components/base';
import Transition from '@/components/Common/Transition';
import keyboardNavigatorUtils from '@/components/KeyboardNavigator/keyboardNavigatorUtils';
import type {
  KeyboardNavigatorKeymap,
  SelectedNavigatorItem,
} from '@/components/KeyboardNavigator/types';
import AMP_KEYS from '@/configs/amp-track-keys';
import useColor from '@/hooks/domain/useColor';
import { useOnClickOutside } from '@/hooks/pure/useOnClickOutside';
import { track } from '@/utils/track/track';
import { isEmptyString } from '@/utils/util';

import DefaultNavigatorView from './DefaultNavigatorView';
import useNavigatorHistory from './hooks/useNavigatorHistory';
import useNavigatorQueryData from './hooks/useNavigatorQueryData';
import type { NavigatorBaseItem } from './types/navigatorTypes';

const allowKeyList = [
  'ArrowDown',
  'ArrowUp',
  'ArrowRight',
  'ArrowLeft',
  'Enter',
];

type Props = {
  onSubmitCallback?: (item: NavigatorBaseItem) => void;
  isLoading?: boolean;
  sizeType?: 'm' | 'l';
  onOpenStateChange?: (isOpen: boolean) => void;
};

const Navigator = (props: Props) => {
  const { sizeType = 'l' } = props;
  const ref = useRef<HTMLDivElement>(null);
  const color = useColor();
  const [openState, setOpenState] = useState(false);
  const debounceState = useRef(false);

  const [input, setInput] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');

  const [selectedItem, setSelectedItem] =
    useState<SelectedNavigatorItem | null>(null);

  const { data, isLoading } = useNavigatorQueryData(debouncedInput);
  const { history, addHistory } = useNavigatorHistory();

  const debounce = useDebouncedCallback(async (value: string) => {
    if (!debounceState.current) return;
    setDebouncedInput(value);
    debounceState.current = false;
  }, 200);

  const submit = useCallback(
    async (item: NavigatorBaseItem) => {
      track(AMP_KEYS.NAVIGATOR_SELECT, {
        target: item.target,
        name: item.name,
        alias: item.alias,
      });
      addHistory({
        alias: item.alias,
        target: item.target,
        name: item.name,
        href: item.href,
        path: item.path,
        hidden: item.hidden,
      });
      props.onSubmitCallback?.(item);
    },
    [props.onSubmitCallback],
  );

  const keymap: KeyboardNavigatorKeymap | null = useMemo(() => {
    if (input === '')
      return [
        history.map((history, index) => ({
          index,
          action: () => {
            submit(history);
          },
        })),
      ];

    const initialize = () => {
      setInput('');
      debounceState.current = false;
      setDebouncedInput('');
      setSelectedItem(null);
    };

    const col0 = [
      ...(data?.map((item, index) => ({
        index,
        action: () => {
          submit(item);
          initialize();
        },
      })) || []),
    ];

    return [col0];
  }, [input, data, submit]);

  const onInputKeyDown = useThrottledCallback(({ key, value }) => {
    keyboardNavigatorUtils.keyboardNavigationEventHandler({
      key,
      keyboardNavigatorKeymap: keymap,
      selectedItem,
      setSelectedItem,
      value,
    });
  }, 100);

  useEffect(() => {
    setSelectedItem({
      col: 0,
      index: 0,
    });
  }, [debouncedInput]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (allowKeyList.includes(e.key)) {
        e.preventDefault();
        e.stopPropagation();
        onInputKeyDown({
          key: e.key,
          value: (e.target as HTMLInputElement).value,
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onInputKeyDown]);

  useEffect(() => {
    const condition = !!input;
    if (condition) {
      setOpenState(true);
      props.onOpenStateChange?.(true);
    } else {
      if (history.length > 0) {
        setOpenState(true);
        props.onOpenStateChange?.(true);
      } else {
        setOpenState(false);
        props.onOpenStateChange?.(false);
      }
    }
  }, [history, props.onOpenStateChange, input]);

  useOnClickOutside(ref, () => {
    setOpenState(false);
    props.onOpenStateChange?.(false);
  });

  return (
    <Style
      ref={ref}
      isLoading={props.isLoading}
      sizeType={sizeType}
      css={
        openState
          ? css`
              min-height: 400px;
              height: auto;
              box-shadow: 0px 4px 24px ${(props) => props.theme.colors.FD08};
            `
          : css`
              min-height: ${sizeType === 'l' ? '60px' : '48px'};
              height: ${sizeType === 'l' ? '60px' : '48px'};
            `
      }
    >
      <Flex
        g={sizeType === 'l' ? 16 : 12}
        a="center"
        className="search-input-area"
      >
        <CompassOutlined
          style={{
            fontSize: sizeType === 'l' ? 24 : 20,
            color: color.primary100,
          }}
        />
        <Flex className="input-wrapper" f={1} a="center">
          <input
            autoFocus
            style={{ minWidth: 50, flex: 1, minHeight: 40 }}
            placeholder={
              'Navigate to Topics, Charts, Dashboards, Menus and Settings'
            }
            value={input}
            disabled={props.isLoading}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setInput(value);
              debounceState.current = true;
              debounce(value);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
              if (allowKeyList.includes(e.key)) {
                e.preventDefault();
                if (e.nativeEvent.isComposing) return;
                onInputKeyDown({
                  key: e.key,
                  value: e.currentTarget.value,
                });
              }
            }}
          />
        </Flex>
      </Flex>

      <Transition
        css={css`
          display: flex;
          flex-direction: column;
          flex: 1;
          height: 100%;
          overflow: auto;
        `}
        status={openState}
      >
        <Divider />
        {keymap && (
          <DefaultNavigatorView
            selectedItem={selectedItem}
            setSelectedItem={setSelectedItem}
            keymap={keymap}
            debouncedInput={debouncedInput}
            data={isEmptyString(debouncedInput) ? history : data}
            isLoading={isLoading}
          />
        )}
      </Transition>
    </Style>
  );
};

const Style = styled(VFlex)<{ isLoading?: boolean; sizeType?: 'm' | 'l' }>`
  width: 100%;
  background-color: ${(props) => props.theme.colors.gray100};
  border-radius: ${(props) => props.theme.radius.token.xl}px;
  transition: all 0.2s ease-in-out;
  overflow: unset;
  border: 1px solid ${(props) => props.theme.colors.gray400};
  max-height: 400px;
  ${({ isLoading }) =>
    isLoading &&
    css`
      background-color: ${(props) => props.theme.colors.gray250};
    `}
  .search-input-area {
    padding: ${(props) =>
      props.sizeType === 'l' ? '10px 10px 10px 16px' : '3px 10px 3px 12px'};

    .input-wrapper {
      width: 100%;
      flex-wrap: wrap;
      min-width: 40px;
      align-items: center;
      input {
        width: 100%;
        border: none;
        background-color: transparent;
        height: 24px;
        color: ${(props) => props.theme.colors.gray800};
        font-size: 16px;
        &::placeholder {
          color: ${(props) => props.theme.colors.gray500};
        }
        &:focus {
          outline: none;
        }
      }
    }
  }
`;

export default Navigator;
